import {
  generateFormDefaultValues,
  generateValidationRules,
  parseAdditionalFormField,
  parseFormError,
  type DynamicForm,
} from '@/utils/forms';
import { match } from 'ts-pattern';
import { formOptions } from '@tanstack/react-form';
import { useAppForm } from './form';
import { Suspense, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { cmsFormSubmit } from '@/services/apis/cmsFormSubmit';
import { apiMutationKeys } from '@/services/apis/apiMutationKeys';
import { FORM_MESSAGE } from '@/constants/form.constants';
import type { APIMessage } from './form-alert';
import { FormAlert } from './form-alert';
import { FormAPIError } from '@/services/apis/cmsFormSubmit/FormAPIError';
import { SCROLLABLE_IDS } from '@/constants/app.constants';
interface CMSFormBuilderProps {
  formData: DynamicForm;
}

const CMSFormBuilder = ({ formData }: CMSFormBuilderProps) => {
  const url = formData.meta.submitButton.default;
  const { mutateAsync } = useMutation({
    mutationFn: cmsFormSubmit,
    mutationKey: apiMutationKeys.cmsFormSubmit(url),
  });

  const [apiMessage, setAPIMessage] = useState<APIMessage>({
    message: [],
    isError: false,
  });

  const closeAPIMessage = () => {
    setAPIMessage({ message: [], isError: false });
  };

  const formDefaultValues = generateFormDefaultValues(formData.formFields);
  const additionalField = parseAdditionalFormField(
    formData.meta.addititionalField.default
  );

  const formOpts = formOptions({
    defaultValues: formDefaultValues,
    onSubmit: async ({ value, formApi }) => {
      try {
        await mutateAsync({
          url: url,
          body: { ...value, ...(additionalField && additionalField) },
          method: formData.meta.method.default,
        });

        setAPIMessage({
          message: FORM_MESSAGE.API_DEFAULT_SUCCESS_MESSAGE,
          isError: false,
        });

        formApi.reset();
      } catch (e) {
        if (!(e instanceof FormAPIError) || !e.errors) {
          setAPIMessage({
            message: FORM_MESSAGE.API_DEFAULT_ERROR_MESSAGE,
            isError: true,
          });
          return;
        }

        setAPIMessage({ message: parseFormError(e), isError: true });
      }
    },
  });

  const form = useAppForm({
    ...formOpts,
  });
  return (
    <>
      <div
        id={SCROLLABLE_IDS.FORMS}
        className="my-6 md:my-10 lg:mt-16 scroll-mt-[200px]"
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
          className="space-y-2"
        >
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {formData.formFields.map((item, index) =>
              match(item)
                .with({ type: 'text' } as const, (formField) => (
                  <form.AppField
                    key={index}
                    name={formField.handle}
                    validators={{
                      onChange: generateValidationRules(formField),
                    }}
                    children={(field) => {
                      return (
                        <Suspense fallback={<div>Loading text input...</div>}>
                          <field.CMSInputText formField={formField} />
                        </Suspense>
                      );
                    }}
                  />
                ))
                .with({ type: 'textarea' } as const, (formField) => (
                  <form.AppField
                    key={index}
                    name={formField.handle}
                    validators={{
                      onChange: generateValidationRules(formField),
                    }}
                    children={(field) => {
                      return (
                        <Suspense
                          fallback={<div>Loading textarea input...</div>}
                        >
                          {' '}
                          <field.CMSInputTextArea formField={formField} />{' '}
                        </Suspense>
                      );
                    }}
                  />
                ))
                .with({ type: 'select' } as const, (formField) => (
                  <form.AppField
                    key={index}
                    name={formField.handle}
                    validators={{
                      onChange: generateValidationRules(formField),
                    }}
                    children={(field) => {
                      return (
                        <Suspense fallback={<div>Loading select input...</div>}>
                          {' '}
                          <field.CMSSelect formField={formField} />{' '}
                        </Suspense>
                      );
                    }}
                  />
                ))
                .otherwise(() => <div>Unhandled CMS Form Field</div>)
            )}
          </div>
          <form.AppForm>
            <form.CMSFormSubmit
              text={formData.meta.submitButton.display ?? 'Submit'}
            ></form.CMSFormSubmit>
          </form.AppForm>
        </form>
      </div>
      {apiMessage.message.length > 0 && (
        <FormAlert handleClose={closeAPIMessage} apiMessage={apiMessage} />
      )}
    </>
  );
};

export { CMSFormBuilder };
